import React, { useState, useEffect } from 'react';
import { createEvent, previewEvent } from '../apis/eventsApi';
import RecurrenceBuilder from './RecurrenceBuilder';
import SelectBranch from './selectBranch';
import { fetchBranches } from '../apis/branchApi';
import { fetchClassTypes } from '../apis/classTypeApi';
import { uploadImage, fetchImages } from '../apis/uploadApi';
import { Form, Input, DatePicker, InputNumber, Button, Select, Radio, Checkbox, Tooltip, Modal, List, Badge, Space, notification, Upload, message } from 'antd';
import Title from 'antd/es/typography/Title';
import { QuestionCircleOutlined, UploadOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
dayjs.extend(utc);

export default function EventForm({ onSuccess, initialData, onCustomSubmit, hideTypeSelector, isModal }){
  const [form] = Form.useForm();
  const [branches, setBranches] = useState([]);
  const [classTypes, setClassTypes] = useState([]);
  const [branchId, setBranchId] = useState('');
  const [classTypeId, setClassTypeId] = useState('');
  const [recurrence, setRecurrence] = useState({});
  const [previewVisible, setPreviewVisible] = useState(false);
  const [previewDates, setPreviewDates] = useState([]);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const [pendingPayload, setPendingPayload] = useState(null);
  const [availableImages, setAvailableImages] = useState([]);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);

  const wrapperClass = isModal ? "w-100 mt-3" : "input-group col-10 col-md-5 p-0 mx-auto mt-3";

  // Load initial data when editing
  useEffect(() => {
    if (initialData) {
      form.setFieldsValue({
        type: initialData.type || (initialData.is_unique ? 'unique' : 'recurring'),
        name: initialData.name,
        place: initialData.place,
        image: initialData.image,
        // show price in pesos (cents -> pesos)
        price: initialData.price_in_cents ? initialData.price_in_cents / 100 : undefined,
        member_extra_price: initialData.extra_price_in_cents_for_members ? initialData.extra_price_in_cents_for_members / 100 : undefined,
        quotas: initialData.quotas,
        discounts_session: initialData.discounts_session !== undefined ? !!initialData.discounts_session : true,
        enabled_for_courtesy: !!initialData.enabled_for_courtesy,
        ignore_schedule_restrictions: initialData.enabled_schedule_restrictions !== undefined ? !initialData.enabled_schedule_restrictions : false,
      });
      if (initialData.branch_id) setBranchId(initialData.branch_id);
      if (initialData.class_type_id) setClassTypeId(initialData.class_type_id);
    }
  }, [initialData, form]);

  // Load branches, class types, and images
  useEffect(() => {
    const load = async () => {
      try {
        const b = await fetchBranches();
        setBranches(b || []);
      } catch (e) {
        console.error('Error loading branches', e);
      }
      try {
        const res = await fetchClassTypes();
        if (res.success) setClassTypes(res.data || []);
      } catch (e) {
        console.error('Error loading class types', e);
      }
      try {
        const imgRes = await fetchImages();
        if (imgRes.success) setAvailableImages(imgRes.images || []);
      } catch (e) {
        console.error('Error loading images', e);
      }
    };
    load();
  }, []);

  // Restore draft from localStorage on mount
  useEffect(() => {
    if (initialData) return; // Don't restore draft when editing
    const savedDraft = localStorage.getItem('event_form_draft');
    if (savedDraft) {
      try {
        const parsed = JSON.parse(savedDraft);
        // Convert ISO date strings back into dayjs objects for form inputs
        if (parsed.start_datetime) parsed.start_datetime = dayjs(parsed.start_datetime);
        if (parsed.end_datetime) parsed.end_datetime = dayjs(parsed.end_datetime);
        if (parsed.start_time) parsed.start_time = dayjs(parsed.start_time);
        if (parsed.end_time) parsed.end_time = dayjs(parsed.end_time);
        if (parsed.first_recurrence) parsed.first_recurrence = dayjs(parsed.first_recurrence);
        if (parsed.end_recurrency) parsed.end_recurrency = dayjs(parsed.end_recurrency);
        
        form.setFieldsValue(parsed);
        if (parsed.branch_id) setBranchId(parsed.branch_id);
        if (parsed.class_type_id) setClassTypeId(parsed.class_type_id);
      } catch (e) {
        console.error('Error restoring draft:', e);
      }
    }
  }, [form, initialData]);

  // Save draft to localStorage on values change
  const handleValuesChange = (_, allValues) => {
    if (initialData) return; // Don't save draft when editing
    // Include branchId and classTypeId in the draft
    const draftData = { ...allValues, branch_id: branchId, class_type_id: classTypeId };
    localStorage.setItem('event_form_draft', JSON.stringify(draftData));
  };

  // Clear form and delete draft
  const handleClearForm = () => {
    form.resetFields();
    setBranchId('');
    setClassTypeId('');
    setRecurrence({});
    setSelectedFile(null);
    localStorage.removeItem('event_form_draft');
    notification.info({
      message: 'Borrador limpiado',
      description: 'El formulario se ha reseteado y el borrador local fue eliminado.',
      placement: 'topRight',
    });
  };

  const handleBeforeUpload = (file) => {
    setSelectedFile(file);
    const tempImg = { url: file.name, name: `[Nueva] ${file.name}`, isTemp: true };
    setAvailableImages(prev => {
      const filtered = prev.filter(img => !img.isTemp);
      return [...filtered, tempImg];
    });
    form.setFieldsValue({ image: file.name });
    return false; // Prevent automatic upload
  };

  const handleFinish = async (values) => {
    setPreviewLoading(true);
    const q = parseInt(values.quotas || values.capacity || 0, 10) || 0;
    const payload = {
      type: values.type || 'unique',
      name: values.name,
      branch_id: branchId,
      place: values.place,
      image: values.image,
      quotas: q,
      class_type_id: classTypeId,
      price_in_cents: Math.round((values.price || 0) * 100),
      extra_price_in_cents_for_members: Math.round((values.member_extra_price || 0) * 100),
      discounts_session: values.discounts_session ? 1 : 0,
      enabled_for_courtesy: values.enabled_for_courtesy ? 1 : 0,
      enabled_schedule_restrictions: values.ignore_schedule_restrictions ? 0 : 1,
    };

    if (values.type === 'unique') {
      const s_dt = values.start_datetime ? dayjs(values.start_datetime) : null;
      const e_dt = values.end_datetime ? dayjs(values.end_datetime) : null;

      if (s_dt && s_dt.isBefore(dayjs())) {
        notification.warning({ message: 'El inicio no puede ser en el pasado', placement: 'topRight' });
        setPreviewLoading(false);
        return;
      }
      if (s_dt && e_dt && e_dt.isSameOrBefore(s_dt)) {
        notification.warning({ message: 'La hora de fin debe ser después de la hora de inicio', placement: 'topRight' });
        setPreviewLoading(false);
        return;
      }

      // format datetimes as YYYY-MM-DDTHH:mm:ss in UTC
      const s = s_dt ? s_dt.utc().format('YYYY-MM-DDTHH:mm:ss') : null;
      const e = e_dt ? e_dt.utc().format('YYYY-MM-DDTHH:mm:ss') : null;
      payload.start_datetime = s;
      payload.end_datetime = e;
      payload.skips_closures = values.skips_closures ? 1 : 0;
    } else {
      if (recurrence.frequency === 'WEEK' && (!recurrence.days_of_week || !recurrence.days_of_week.toString().split(',').filter(Boolean).length)) {
        notification.warning({ message: 'Selecciona al menos un día de la semana para la recurrencia semanal', placement: 'topRight' });
        setPreviewLoading(false);
        return;
      }
      if (recurrence.frequency === 'MONTH' && recurrence.week_of_month && (!recurrence.days_of_week || !recurrence.days_of_week.toString().split(',').filter(Boolean).length)) {
        notification.warning({ message: 'Selecciona el día de la semana para la recurrencia mensual por semana del mes', placement: 'topRight' });
        setPreviewLoading(false);
        return;
      }

      payload.frequency = recurrence.frequency;
      payload.interval = recurrence.interval;
      payload.days_of_week = recurrence.days_of_week ? recurrence.days_of_week.toString() : null;
      if (recurrence.day_of_month !== undefined) payload.day_of_month = recurrence.day_of_month;
      if (recurrence.week_of_month !== undefined) payload.week_of_month = recurrence.week_of_month;
      if (recurrence.month_of_year !== undefined) payload.month_of_year = recurrence.month_of_year;
      if (recurrence.last_of_month !== undefined) payload.last_of_month = recurrence.last_of_month;

      const localFirstRecStr = recurrence.first_recurrence || dayjs().format('YYYY-MM-DD');
      const localStartTimeStr = recurrence.start_time || '00:00';
      const localEndTimeStr = recurrence.end_time || '00:00';

      const localStartDt = dayjs(`${localFirstRecStr}T${localStartTimeStr}`);
      let localEndDt = dayjs(`${localFirstRecStr}T${localEndTimeStr}`);
      if (localEndDt.isBefore(localStartDt)) localEndDt = localEndDt.add(1, 'day');

      const utcStartDt = localStartDt.utc();
      const utcEndDt = localEndDt.utc();

      payload.start_time = utcStartDt.format('HH:mm:ss');
      payload.end_time = utcEndDt.format('HH:mm:ss');

      if (dayjs(`${localFirstRecStr}T${localStartTimeStr}`).isBefore(dayjs())) {
        notification.warning({ message: 'La primera ocurrencia no puede ser en el pasado', placement: 'topRight' });
        setPreviewLoading(false);
        return;
      }

      if (dayjs(`1970-01-01T${localEndTimeStr}`).isSameOrBefore(dayjs(`1970-01-01T${localStartTimeStr}`))) {
        notification.warning({ message: 'La hora de fin debe ser después de la hora de inicio', placement: 'topRight' });
        setPreviewLoading(false);
        return;
      }

      if (recurrence.first_recurrence) {
        payload.first_recurrence = utcStartDt.format('YYYY-MM-DD');
      }
      if (recurrence.end_recurrency) {
        if (dayjs(recurrence.end_recurrency).isBefore(dayjs(localFirstRecStr))) {
          notification.warning({ message: 'La última ocurrencia no puede ser antes de la primera ocurrencia', placement: 'topRight' });
          setPreviewLoading(false);
          return;
        }
        const localEndRecDt = dayjs(`${recurrence.end_recurrency}T${localStartTimeStr}`);
        payload.end_recurrency = localEndRecDt.utc().format('YYYY-MM-DDTHH:mm:ss');
      }
    }

    setPendingPayload(payload);

    // If custom submit handler (e.g. editing), use it directly
    if (onCustomSubmit) {
      try {
        let finalPayload = { ...payload };
        if (selectedFile) {
          setUploadingImage(true);
          const res = await uploadImage(selectedFile);
          if (res.success) {
            finalPayload.image = res.url;
          } else {
            throw new Error('Error al subir imagen antes de actualizar el evento');
          }
        }
        await onCustomSubmit(finalPayload);
      } catch (err) {
        console.error(err);
        notification.error({
          message: 'Error',
          description: err.response?.data?.error || err.message,
          placement: 'topRight',
        });
      } finally {
        setPreviewLoading(false);
        setUploadingImage(false);
      }
      return;
    }

    // Call preview endpoint
    try {
      const res = await previewEvent(payload);
      setPreviewDates(res.data || []);
      setPreviewVisible(true);
    } catch (err) {
      console.error(err);
      notification.error({
        message: 'Error de vista previa',
        description: err.response?.data?.error || err.message,
        placement: 'topRight',
      });
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleSaveEvent = async () => {
    if (!pendingPayload) return;
    setSaveLoading(true);
    try {
      let finalPayload = { ...pendingPayload };
      if (selectedFile) {
        setUploadingImage(true);
        const res = await uploadImage(selectedFile);
        if (res.success) {
          finalPayload.image = res.url;
        } else {
          throw new Error('Error al subir imagen antes de guardar el evento');
        }
      }
      const res = await createEvent(finalPayload);
      if (onSuccess) onSuccess(res.data);
      notification.success({
        message: 'Evento creado',
        description: 'El evento ha sido guardado exitosamente.',
        placement: 'topRight',
      });
      form.resetFields();
      setBranchId('');
      setClassTypeId('');
      setRecurrence({});
      localStorage.removeItem('event_form_draft');
      setPreviewVisible(false);
      setPendingPayload(null);
      setSelectedFile(null);
    } catch (err) {
      console.error(err);
      notification.error({
        message: 'Error creando evento',
        description: err.response?.data?.error || err.message,
        placement: 'topRight',
      });
    } finally {
      setSaveLoading(false);
      setUploadingImage(false);
    }
  };

  return (
    <>
      <Form
        form={form}
        name="eventForm"
        className={isModal ? "w-100" : "mobile-block themed-block p-3"}
        layout="vertical"
        onFinish={handleFinish}
        onValuesChange={handleValuesChange}
        autoComplete="off"
        preserve={false}
      >
        {!isModal && <Title level={2} className="text-center">Crear evento</Title>}

        {!hideTypeSelector && (
          <div className={wrapperClass}>
            <Form.Item name="type" initialValue="unique" className="w-100 mb-0 text-center">
              <Radio.Group>
                <Radio value="unique">Único</Radio>
                <Radio value="recurring">Recurrente</Radio>
              </Radio.Group>
            </Form.Item>
          </div>
        )}

        <div className={wrapperClass}>
          <label className="form-label">Nombre del evento *:</label>
          <Form.Item name="name" rules={[{ required: true, message: 'Por favor ingresa el nombre' }]} className="w-100 mb-0">
            <Input className='m-0' />
          </Form.Item>
        </div>

        <div className={wrapperClass}>
          <SelectBranch branches={branches} onChange={value => setBranchId(value)} value={branchId} />
        </div>

        <div className={wrapperClass}>
          <label className="form-label">Lugar *:</label>
          <Form.Item name="place" rules={[{ required: true, message: 'Por favor ingresa el lugar' }]} className="w-100 mb-0">
            <Input className='m-0' placeholder="Ej. Salón Principal" />
          </Form.Item>
        </div>

        <div className={wrapperClass}>
          <label className="form-label">Imagen *:</label>
          <div className="w-100 d-flex gap-2">
            <Form.Item name="image" rules={[{ required: true, message: 'Selecciona una imagen' }]} className="w-100 mb-0">
              <Select
                className="w-100"
                placeholder="Selecciona una imagen existente"
                options={availableImages.map(img => ({
                  value: img.url,
                  label: (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <img src={img.url} alt={img.name} style={{ width: 24, height: 24, objectFit: 'cover', borderRadius: 4 }} />
                      <span>{img.name}</span>
                    </div>
                  ),
                  name: img.name
                }))}
                onChange={(value) => {
                  if (value !== selectedFile?.name) {
                    setSelectedFile(null);
                  }
                }}
                filterOption={(input, option) =>
                  (option?.name ?? '').toLowerCase().includes(input.toLowerCase())
                }
                allowClear
                showSearch
              />
            </Form.Item>
            <Upload beforeUpload={handleBeforeUpload} showUploadList={false} accept="image/*">
              <Button icon={<UploadOutlined />} loading={uploadingImage}>Subir</Button>
            </Upload>
          </div>
        </div>

        <div className={wrapperClass}>
          <label className="form-label">Tipo de clase *:</label>
          <Select
            className="w-100"
            placeholder="Selecciona tipo de clase"
            value={classTypeId || undefined}
            onChange={val => setClassTypeId(val)}
            options={classTypes.map(ct => ({ value: ct.id, label: ct.type }))}
            allowClear
          />
        </div>

        <div className={wrapperClass}>
          <label className="form-label">Precio (No inscritas) *:</label>
          <Form.Item name="price" rules={[{ required: true, message: 'Por favor ingresa el precio' }]} className="w-100 mb-0">
            <InputNumber
              className="w-100"
              min={0}
              formatter={value =>
                value
                  ? value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.')
                  : ''
              }
              parser={value => value ? value.replace(/\./g, '') : ''}
            />
          </Form.Item>
        </div>

        <div className={wrapperClass}>
          <label className="form-label">Precio extra para miembros (Inscritas):</label>
          <Form.Item name="member_extra_price" className="w-100 mb-0">
            <InputNumber
              className="w-100"
              min={0}
              formatter={value =>
                value
                  ? value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.')
                  : ''
              }
              parser={value => value ? value.replace(/\./g, '') : ''}
            />
          </Form.Item>
        </div>

        <div className={wrapperClass}>
          <label className="form-label">Capacidad de usuarios *:</label>
          <Form.Item name="quotas" rules={[{ required: true, message: 'Ingresa la capacidad' }]} className="w-100 mb-0">
            <InputNumber className="w-100" min={0} />
          </Form.Item>
        </div>

        <div className={wrapperClass}>
          <div className="w-100 d-flex flex-column gap-2">
            <Form.Item name="discounts_session" valuePropName="checked" initialValue={true} className="mb-0">
              <Checkbox>
                Descuenta sesión{' '}
                <Tooltip title="Si se activa, se descontará una sesión del plan activo de la alumna.">
                  <QuestionCircleOutlined style={{ color: '#8c8c8c', marginLeft: 4, cursor: 'pointer' }} />
                </Tooltip>
              </Checkbox>
            </Form.Item>

            <Form.Item name="enabled_for_courtesy" valuePropName="checked" initialValue={false} className="mb-0">
              <Checkbox>
                Habilitada para cortesías
              </Checkbox>
            </Form.Item>
              
            <Form.Item name="ignore_schedule_restrictions" valuePropName="checked" initialValue={false} className="mb-0">
              <Checkbox>
                Ignorar restricciones de horario{' '}
                <Tooltip title="Si se activa, va a ignorar las restricciones de horario en que pueden agendar los planes, por ejemplo el plan morning podría agendar el evento.">
                  <QuestionCircleOutlined style={{ color: '#8c8c8c', marginLeft: 4, cursor: 'pointer' }} />
                </Tooltip>
              </Checkbox>
            </Form.Item>
          </div>
        </div>

        {/* Unique vs Recurring date fields */}
        <Form.Item shouldUpdate={(prev, cur) => prev.type !== cur.type || prev.start_datetime !== cur.start_datetime} className="m-0 p-0 w-100">
          {({ getFieldValue }) => {
            const type = getFieldValue('type');
            const start = getFieldValue('start_datetime');
            return type === 'unique' ? (
              <>
                <div className={wrapperClass}>
                  <label className="form-label">Fecha y hora de inicio *:</label>
                  <Form.Item name="start_datetime" rules={[
                    { required: true, message: 'Selecciona inicio' },
                    () => ({
                      validator(_, value) {
                        if (!value || value.isAfter(dayjs())) {
                          return Promise.resolve();
                        }
                        return Promise.reject(new Error('El inicio no puede ser en el pasado'));
                      },
                    }),
                  ]} className="w-100 mb-0">
                    <DatePicker showTime className="w-100" format="YYYY-MM-DD HH:mm" needConfirm={false} disabledDate={(current) => current && current < dayjs().startOf('day')}/>
                  </Form.Item>
                </div>
                <div className={wrapperClass}>
                  <label className="form-label">Fecha y hora de fin *:</label>
                  <Form.Item name="end_datetime" rules={[
                    { required: true, message: 'Selecciona fin' },
                    () => ({
                      validator(_, value) {
                        if (!value || !start || value.isAfter(start)) {
                          return Promise.resolve();
                        }
                        return Promise.reject(new Error('El fin debe ser después del inicio'));
                      },
                    }),
                  ]} className="w-100 mb-0">
                    <DatePicker showTime className="w-100" format="YYYY-MM-DD HH:mm" needConfirm={false} disabledDate={(current) => {
                      if (!start) return current && current < dayjs().startOf('day');
                      return current && current < start.startOf('day');
                    }}/>
                  </Form.Item>
                </div>
                <div className={wrapperClass}>
                  <Form.Item name="skips_closures" valuePropName="checked" initialValue={false} className="mb-0">
                    <Checkbox>
                      Se salta los cerramientos{' '}
                      <Tooltip title="Si se activa, el evento se programará/mostrará aun si la sede está cerrada por festivo o mantenimiento.">
                        <QuestionCircleOutlined style={{ color: '#8c8c8c', marginLeft: 4, cursor: 'pointer' }} />
                      </Tooltip>
                    </Checkbox>
                  </Form.Item>
                </div>
              </>
            ) : (
              <div className={isModal ? "w-100 mt-3" : "input-group col-10 col-md-8 p-0 mx-auto mt-3"}>
                <RecurrenceBuilder onChange={setRecurrence} initialData={initialData} />
              </div>
            )
          }}
        </Form.Item>

        <div className={isModal ? "w-100 mt-3 d-flex justify-content-center gap-3" : "input-group col-10 col-md-5 p-0 mx-auto mt-4 d-flex justify-content-center gap-3"}>
          {!isModal && (
            <Button onClick={handleClearForm}>
              Limpiar Formulario
            </Button>
          )}
          <Button type="primary" className='themed-btn' htmlType="submit" loading={previewLoading}>
            {isModal ? 'Guardar' : 'Crear'}
          </Button>
        </div>
      </Form>

      {/* Verification Modal for Occurrence Preview */}
      <Modal
        title="Validar Ocurrencias del Evento"
        open={previewVisible}
        onOk={handleSaveEvent}
        onCancel={() => setPreviewVisible(false)}
        okText="Confirmar y Guardar"
        cancelText="Cancelar"
        confirmLoading={saveLoading}
        width={600}
      >
        <p>
          Revisa las siguientes ocurrencias programadas para el evento <strong>{pendingPayload?.name}</strong>.
          Si la información es correcta, haz clic en <strong>Confirmar y Guardar</strong>.
        </p>
        <div 
          style={{ 
            maxHeight: '300px', 
            overflowY: 'auto', 
            border: '1px solid #f0f0f0', 
            borderRadius: '8px', 
            padding: '12px',
            backgroundColor: '#fafafa'
          }}
        >
          {previewDates.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '20px', color: '#999' }}>
              No se generarán ocurrencias para este rango de fechas.
            </div>
          ) : (
            <List
              dataSource={previewDates}
              renderItem={(item, idx) => {
                // Parse the server-provided datetimes as UTC and convert to local time
                const sDate = item.start_date ? dayjs.utc(item.start_date).local() : null;
                const eDate = item.end_date ? dayjs.utc(item.end_date).local() : null;
                const text = sDate && eDate
                  ? `${sDate.format('DD/MM/YYYY HH:mm')} - ${eDate.format('HH:mm')}`
                  : sDate ? sDate.format('DD/MM/YYYY HH:mm') : '';

                return (
                  <List.Item key={idx} style={{ padding: '8px 0' }}>
                    <Space style={{ width: '100%', justifyContent: 'space-between' }}>
                      <span>{text}</span>
                      {item.is_closed && (
                        <Badge
                          status={item.is_skipped ? "error" : "warning"}
                          text={item.is_skipped ? "Sede Cerrada (Se saltará)" : "Sede Cerrada (Se programará)"}
                        />
                      )}
                    </Space>
                  </List.Item>
                );
              }}
            />
          )}
        </div>
      </Modal>
    </>
  );
}
